import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { logger } from "@/lib/default-logger";

/**
 * Campaign requests are the client-facing intake for a new campaign: the client describes what
 * they want, we price it, they confirm the price, and only then does it become a programme with
 * one campaign per market. Every screen in `new-pages/campaign-requests` reads and writes
 * through this module — it is the single description of the API's shape on the client.
 *
 * Two rules run through the whole file.
 *
 * Paths never start with `/api`. The axios baseURL already ends in it, so `/api/campaign-requests`
 * resolves to `/api/api/campaign-requests` and 404s.
 *
 * Decimal columns are typed `string`, never `number`. Money and quantities cross the wire exactly
 * as Postgres holds them ("2500.0000", "12500.00") so nothing rounds through a double on the way
 * through. Format them with `formatMoney` / `formatQuantity` from the feature's constants, which
 * coerce once at the edge, rather than calling number methods on them.
 */

// ---------------------------------------------------------------------------------------------
// Enumerations — mirrors of `src/modules/campaign-requests/types` on the API
// ---------------------------------------------------------------------------------------------

/** Where a request sits in its commercial lifecycle. Deliberately linear. */
export type RequestState =
	| "draft"
	| "submitted"
	| "quoted"
	| "awaiting_confirmation"
	| "confirmed"
	| "in_preparation"
	| "ready"
	| "running"
	| "completed"
	| "changes_requested"
	| "cancelled";

/**
 * Something outstanding. Blockers are not states: a request moves through quoting and
 * confirmation while they are open, and only the move to `ready` requires them cleared.
 *
 * `voice_request` is the only one of these that is ours rather than theirs — the client has said
 * none of the curated voices suit and asked for more, and nothing they can do moves it.
 */
export type RequestBlocker =
	| "leads_missing"
	| "flow_missing"
	| "quote_unconfirmed"
	| "sow_missing"
	| "po_missing"
	| "voice_request";

/** What the campaign is for. Chooses the outcome set the client starts editing from. */
export type RequestObjective =
	| "discovery_qualification"
	| "appointment_setting"
	| "lead_reactivation"
	| "event_invitation"
	| "renewal_retention"
	| "research"
	| "other";

/**
 * What has to happen for a language to have an agent. Keyed by language, never by market —
 * one English agent answers Singapore and Australia, and pricing it per market bills it twice.
 */
export type RequestAgentKind =
	| "agent_setup"
	| "translated_agent"
	| "regional_deployment"
	| "agent_revision"
	| "reuse_existing";

/** Where a row on the outcome list came from. Three sources stack and the client edits the result. */
export type OutcomeSource = "starter" | "call_flow" | "previous" | "client";

/** What an attached document is. The kind decides which content types the API accepts. */
export type DocumentKind = "call_flow" | "revised_call_flow" | "lead_list" | "sow" | "po" | "dnc_list" | "other";

/** The chargeable things a quote can be made of. */
export type PriceCode =
	| "agent_setup"
	| "translated_agent"
	| "regional_deployment"
	| "agent_revision"
	| "number_rental"
	| "call_time"
	| "carrier_rate"
	| "minimum_charge"
	| "reuse_existing";

/** How a price code is measured. */
export type PriceUnit = "each" | "per_month" | "per_minute";

/** Whose move it is. Null means nobody is waiting — the request is dialling or over. */
export type BlockedOn = "us" | "them";

// ---------------------------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------------------------

/**
 * The work order, as the client sees it.
 *
 * `expectedReachRate` and `expectedCallMinutes` are numeric(5,2) and arrive as decimal strings;
 * the write payload takes them as numbers, which is the one place the two shapes differ.
 */
export interface CampaignRequest {
	id: string;
	title: string;
	clientReference: string | null;
	objective: RequestObjective | null;
	/** BCP 47. Not derived from `markets` — SG and AU share English. */
	languages: string[];
	notes: string | null;
	state: RequestState;
	/** Recomputed on every write and answered from artifacts, never toggled by a screen. */
	blockers: RequestBlocker[];
	/** ISO 3166-1 alpha-2. A derived index of the market rows, kept for the list's filter. */
	markets: string[];
	expectedContacts: number | null;
	/** ISO date, e.g. "2026-09-12". When the client said the lead list would arrive. */
	leadsDueDate: string | null;
	/** Percentage as a decimal string, e.g. "35.00". */
	expectedReachRate: string | null;
	/** Decimal string, e.g. "3.50". */
	expectedCallMinutes: string | null;
	sowRequired: boolean;
	poRequired: boolean;
	sowReference: string | null;
	poReference: string | null;
	stakeholderEmails: string[];
	recordingDisclosure: boolean;
	aiDisclosure: boolean;
	transferNumber: string | null;
	/** Local wall clock, e.g. "09:00:00". */
	transferWindowStart: string | null;
	transferWindowEnd: string | null;
	dncListDocumentId: string | null;
	/** The moment the attestation was made. Ours, never a date the client chose. */
	leadConsentConfirmedAt: string | null;
	estimatesAcknowledgedAt: string | null;
	confirmedByName: string | null;
	confirmedByRole: string | null;
	confirmedIp: string | null;
	confirmedAt: string | null;
	campaignProgrammeId: string | null;
	/** Set only once every market became a campaign. */
	provisionedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

/**
 * The derived facts the list draws its Dates and Cost columns from, and the counts a draft's
 * "resume at step N" is worked out from.
 *
 * On the list resource because the list is where they are read. Working them out in the browser
 * meant opening every visible row's whole detail graph — up to twenty-five `GET
 * /campaign-requests/:id`, each running eight child queries of its own — to fill in two columns,
 * and giving up with a dash past the twenty-fifth row.
 *
 * Facts, not conclusions: which step a draft resumes at and which are ticked is the wizard's
 * question, answered once in `new-pages/campaign-requests/progress.ts` for both screens.
 */
export interface RequestListSummary {
	/** Earliest dial start across the markets, and the latest end. Null while unscheduled. */
	dialStart: string | null;
	dialEnd: string | null;
	/** Decimal string: the confirmed quote's fixed total. Null until one is confirmed. */
	committedTotal: string | null;
	/** The live quote's estimated band, for a request priced but not yet started. */
	estimateLow: string | null;
	estimateHigh: string | null;
	currency: string;
	hasQuote: boolean;
	agentCount: number;
	marketScheduleCount: number;
	/** Of those, the ones carrying both an agent and a dial start date. */
	scheduledMarketCount: number;
	outcomeCount: number;
	/** A lead list is attached and readable — not "a volume was typed". */
	hasLeadList: boolean;
}

/** One row of `GET /campaign-requests`: the request, plus what the list needs to draw it. */
export interface CampaignRequestListRow extends CampaignRequest {
	summary: RequestListSummary;
}

/**
 * One agent decision. One row per language, never per market — the markets a single agent
 * answers ride along on `markets`.
 */
export interface CampaignRequestAgent {
	id: string;
	requestId: string;
	/** BCP 47. The key of the row: the API is unique on (request, language). */
	language: string;
	markets: string[];
	kind: RequestAgentKind;
	label: string;
	/** The agent being reused, revised or redeployed. */
	sourceAgentId: string | null;
	preparedAgentId?: string | null;
	/** The uploaded call flow. Its absence on a new or revised agent is what `flow_missing` reports. */
	callFlowDocumentId: string | null;
	/**
	 * The curated voice this language is getting, from `GET /voices`. A public-schema id with no
	 * foreign key behind it — `voice` is platform-level and this row is not.
	 */
	voiceId: string | null;
	/**
	 * "None of these voices", in the client's own words. Held with no `voiceId` chosen, it raises
	 * the `voice_request` blocker so somebody here sources more; choosing a voice clears it.
	 */
	voiceRequestNote: string | null;
}

/** One market and its schedule. Every time on the row is local to `timezone`. */
export interface CampaignRequestMarket {
	id: string;
	requestId: string;
	market: string;
	timezone: string;
	/** From zero. Position zero is the lead market; the programme reports in its timezone. */
	position: number;
	dialStartDate: string | null;
	dialEndDate: string | null;
	callingWindowStart: string | null;
	callingWindowEnd: string | null;
	/** ISO-8601 weekday numbers, Monday is 1. Empty means no day restriction. */
	callingDays: number[];
	originNumberId: number | null;
	/**
	 * The caller ID as it is dialled, resolved by the API.
	 *
	 * Published alongside the id so a screen can print the number without reading
	 * `GET /phone-numbers`, which is owner-only — the review screen used to fetch that list
	 * purely to prettify a column and 403'd for every `admin` and `manager` on the account.
	 */
	originNumber: string | null;
	redialIntervalHours: number | null;
	maxAttemptsPerPerson: number | null;
	/** Which agent row answers here. */
	agentId: string | null;
	/**
	 * The CRM group holding this market's contacts, and what its campaign dials.
	 *
	 * Null until the lead list has arrived and been imported — a real state rather than a fault. A
	 * market with no group is still provisioned; its campaign is simply created with nobody in it.
	 */
	leadGroupId: string | null;
	/** The campaign this market became. Null until provisioning has run. */
	campaignId: string | null;
}

export interface CampaignRequestOutcome {
	id: string;
	requestId: string;
	name: string;
	description: string;
	isConversion: boolean;
	notifyOnMatch: boolean;
	/** Recorded by the platform itself. Not editable and not deletable. */
	isSystem: boolean;
	source: OutcomeSource;
}

export interface CampaignRequestCaptureField {
	id: string;
	requestId: string;
	title: string;
	/** The definition handed to the post-call extraction, not a note to a colleague. */
	description: string;
	/** The generated template variable. Stable across a rename of the title, on purpose. */
	appearsAs: string;
	enabled: boolean;
}

/** Who hears about a conversion. To and BCC carry `{{variable}}` templates, not addresses. */
export interface CampaignRequestNotification {
	id: string;
	requestId: string;
	toTemplate: string | null;
	bccTemplate: string | null;
	subjectTemplate: string | null;
	clientNote: string | null;
	/**
	 * The client's own body, as the same `{{variable}}` string the other templates hold. Null
	 * follows the platform default rather than storing a copy of it.
	 */
	bodyTemplate: string | null;
	/** A real address: where a notification goes when the lead's own rep column is empty. */
	fallbackEmail: string | null;
	/**
	 * The other way out of the empty-rep warning: don't call those contacts at all. A decision the
	 * client makes, honoured when the list is loaded — not a derived count.
	 */
	excludeContactsWithoutRecipient: boolean;
}

/** What a document is, never what it contains. */
export interface CampaignRequestDocument {
	id: string;
	requestId: string;
	kind: DocumentKind;
	originalFilename: string;
	/** Sniffed from the bytes, not from what the upload declared. */
	mimeType: string;
	sizeBytes: number;
	/** SHA-256 of the plaintext, hex. Published so a client can check their own upload arrived intact. */
	sha256: string;
	uploadedByUserId: string | null;
	createdAt: string;
}

/** One move, as the progress timeline reads it. `actorName` is copied, never resolved at read time. */
export interface CampaignRequestEvent {
	id: string;
	requestId: string;
	fromState: RequestState | null;
	toState: RequestState;
	actorUserId: string | null;
	actorName: string | null;
	reason: string | null;
	at: string;
	/**
	 * Monotonic write order, as a decimal string — it is a `bigint` column, and every numeric
	 * column on this API arrives as a string.
	 *
	 * Sort by `(at, seq)` and never by `at` alone. Confirmation writes two moves inside one
	 * transaction and provisioning writes another two, so they carry the same timestamp to the
	 * microsecond; without the tiebreak the timeline can put a confirmation above the submission
	 * that caused it. Compare it numerically — `"9"` sorts after `"10"` as text.
	 */
	seq: string;
}

/** One priced line. Every decimal is a string; see the note at the top of this file. */
export interface QuoteLine {
	id: string;
	code: PriceCode;
	label: string;
	/** ISO 3166-1 alpha-2 where the charge is market-specific, null where it is not. */
	market: string | null;
	/** Decimal string, e.g. "3.0000". */
	quantity: string;
	/** Decimal string: the rate frozen onto this line at pricing time. */
	unitAmount: string;
	unit: PriceUnit;
	/** Decimal string: quantity × unitAmount. */
	amount: string;
	/** Variable lines are an estimate shown as a range and billed on actuals. */
	isVariable: boolean;
}

export interface Quote {
	id: string;
	/** 1, 2, 3… within a request, so both sides can name a version. */
	version: number;
	currency: string;
	/** Decimal string: the sum of the fixed lines. The firm part. */
	fixedTotal: string;
	variableLow: string;
	variableHigh: string;
	/** The assumptions the variable range was built from, copied in at pricing time. */
	assumptions: Record<string, unknown> | null;
	confirmedAt: string | null;
	supersededAt: string | null;
	createdAt: string;
	lines: QuoteLine[];
}

/**
 * The whole request graph, in one call. Only `GET /campaign-requests/:request` answers this —
 * the wizard resumes from it rather than making nine round trips.
 */
export interface CampaignRequestDetail extends CampaignRequest {
	preparationStatus?: "queued" | "running" | "review" | "approved" | "failed" | null;
	proving?: { status: string; passed: number; total: number } | null;
	agents: CampaignRequestAgent[];
	/** In the client's own order: position zero is the lead market. */
	marketSchedules: CampaignRequestMarket[];
	outcomes: CampaignRequestOutcome[];
	captureFields: CampaignRequestCaptureField[];
	/** Null until the client has configured the notification step. */
	notification: CampaignRequestNotification | null;
	documents: CampaignRequestDocument[];
	/** Oldest first: this is a timeline and it is read top to bottom. */
	events: CampaignRequestEvent[];
	/** The newest quote that has not been superseded. */
	currentQuote: Quote | null;
}

/**
 * One rate, as the account filling in the wizard sees it.
 *
 * Not a price book row, deliberately: `GET /price-book` is cross-account and super-admin only, so
 * a client reading it gets a 403 and step 2 loses the priced options that are the whole mechanism
 * of the step. `GET /campaign-requests/:id/rates` answers the narrow question instead — only the
 * codes this request can incur, only at this account's amounts, with the account taken from the
 * session. A rate still marked a placeholder is absent rather than shown, because quoting refuses
 * to price against one.
 */
export interface CampaignRequestRate {
	code: PriceCode;
	/** The market it was asked for, or null where the charge is not market-specific. */
	market: string | null;
	/** numeric(12,4) as a decimal string. */
	amount: string;
	unit: PriceUnit;
	currency: string;
}

/** One uploaded list, and whether we have been able to read it here. */
export interface LeadFileState {
	documentId: string;
	filename: string;
	sizeBytes: number;
	/** `unreadable` always carries a `reason`. Nothing was counted from that file. */
	status: "checked" | "unreadable";
	rows: number;
	/** The worksheet the rows came from, where the workbook has more than one visible tab. */
	sheet: string | null;
	/** Why nothing could be read, in words the client can act on. Null while `status` is checked. */
	reason: string | null;
}

/** A row as we read it, so the client can check the parse rather than trust it. */
export interface LeadSampleRow {
	file: string;
	/** The row number in the client's own spreadsheet. Row 1 is their header. */
	row: number;
	/** The number as we will dial it. */
	phone: string;
	market: string;
	values: Record<string, string>;
}

/** One line of the "What we found / Rows / Goes to" table. */
export interface LeadMarketSplit {
	/** ISO 3166-1 alpha-2, read off the number's own country code. */
	market: string;
	callingCode: string | null;
	rows: number;
	agentLabel: string | null;
	/** False where the country code is not one of the markets this request runs in. */
	routed: boolean;
}

/** UX §08 note 7: a contact with no rep converts and nobody is told. */
export interface LeadRepColumnCheck {
	variable: string;
	column: string | null;
	missing: number;
	checked: number;
}

export interface LeadAnalysis {
	files: LeadFileState[];
	rowsIn: number;
	/** Rows that survived cleaning. */
	usable: number;
	/**
	 * Of those, the ones that landed in a market this request runs in — the number that actually
	 * gets dialled, and so the number the contact estimate is pre-filled from.
	 */
	routedUsable: number;
	markets: LeadMarketSplit[];
	duplicatesRemoved: number;
	crossMarketDuplicates: number;
	rejected: number;
	/** Contacts left out because the client asked us not to call anyone nobody would be told about. */
	excludedNoRecipient: number;
	/** The column that exclusion reads. Null where it was not asked for, or the list has no such column. */
	recipientColumn: string | null;
	phoneColumn: string | null;
	identityColumn: string | null;
	columns: string[];
	repColumn: LeadRepColumnCheck | null;
	/** A few of the rows we will dial, as we read them. Empty where nothing survived cleaning. */
	sample: LeadSampleRow[];
}

export interface WeekdayReach {
	/** ISO-8601 weekday. Monday is 1. */
	weekday: number;
	dialed: number;
	reached: number;
	reachRate: number;
}

/**
 * The step 3 flag and the evidence under it. Never blocking — canvas §05 rule 1.
 *
 * `weakDays`/`strongDays` are weekday numbers rather than a date because the date being flagged
 * lives in the wizard's own draft and has not been saved: the one-tap fix has to move that one.
 */
export interface MarketGuidance {
	market: string;
	/** `recommendation` means we have not dialled this market enough to quote a number. */
	basis: "observed" | "recommendation";
	sampleSize: number;
	windowDays: number;
	byWeekday: WeekdayReach[];
	weakDays: number[];
	strongDays: number[];
	deltaPercent: number | null;
	additionalConnects: number | null;
	/** "based on 41,200 dials · SG · last 90 days". Printed verbatim under the flag. */
	evidence: string;
}

/** The conversion email resolved against a real row of the uploaded list. */
export interface NotificationPreview {
	to: string | null;
	bcc: string[];
	subject: string;
	body: string;
	/** False where nothing has been uploaded and there was no row to resolve against. */
	fromRealRow: boolean;
	sampleContact: string | null;
	/** The outcome, summary, duration and link are worked examples — no call has happened yet. */
	callIsExample: boolean;
	/** Variables the templates named that the row could not answer. */
	unresolved: string[];
	/** True where this row had no recipient of its own and the fallback address caught it. */
	usedFallback: boolean;
}

export interface PriceBookItem {
	id: string;
	/** Null is the platform default, used when an account has no override. */
	accountId: string | null;
	code: PriceCode;
	market: string | null;
	/** numeric(12,4) as a decimal string, e.g. "1200.0000". */
	amount: string;
	unit: PriceUnit;
	currency: string;
	/** For whoever edits the price book. Never shown to a client. */
	note: string | null;
	createdAt: string;
}

/** One line of the cross-account queue. Flat, because every field below is a column in a table. */
export interface AdminQueueRow {
	requestId: string;
	/** Which schema to open to act on this request. */
	tenantId: string;
	accountId: string | null;
	accountName: string | null;
	title: string;
	clientReference: string | null;
	state: RequestState;
	blockers: RequestBlocker[];
	blockedOn: BlockedOn | null;
	marketCount: number;
	languageCount: number;
	/** Decimal strings off the live quote, or null where the request has never been priced. */
	fixedTotal: string | null;
	variableLow: string | null;
	variableHigh: string | null;
	currency: string | null;
	submittedAt: string | null;
	confirmedAt: string | null;
	leadsDueDate: string | null;
	createdAt: string;
	updatedAt: string;
	/** How long this has been sitting, in seconds, measured in SQL rather than from a browser clock. */
	ageSeconds: number;
	/** Fixed plus the midpoint of the estimated range, as a decimal string. */
	value: string | null;
}

/** One previous campaign as step 0 offers it. */
export interface CloneableRequest {
	id: string;
	title: string;
	clientReference: string | null;
	objective: RequestObjective | null;
	state: RequestState;
	markets: string[];
	marketCount: number;
	/** Earliest dial start across the markets, null where it was never scheduled. */
	ranFrom: string | null;
	ranTo: string | null;
	/** Null means it was configured but never ran — so the screen must not say "ran". */
	provisionedAt: string | null;
	createdAt: string;
}

export interface CloneEntry {
	item: string;
	count: number;
	/** The names behind the count, where naming them is what makes the line trustworthy. */
	detail: string | null;
}

export interface CloneOmission {
	item: string;
	reason: string;
}

/** What a clone carried over, and what it deliberately left. Both halves, because only one reassures. */
export interface CloneManifest {
	source: CloneableRequest;
	copied: CloneEntry[];
	notCopied: CloneOmission[];
}

/** What a request turned into. An empty `created` means everything was already built. */
export interface ProvisionResult {
	programmeId: string;
	campaignIds: string[];
	created: string[];
}

// ---------------------------------------------------------------------------------------------
// Write payloads
// ---------------------------------------------------------------------------------------------

/**
 * Everything a client may set on a request. `state`, `blockers` and the confirmation record are
 * absent on purpose: the API whitelists, so sending them has them stripped rather than honoured.
 */
export interface CampaignRequestPayload {
	title: string;
	clientReference?: string | null;
	objective?: RequestObjective | null;
	languages?: string[];
	notes?: string | null;
	/** At least one if sent at all — an empty array means "run this nowhere" and the API refuses it. */
	markets?: string[];
	expectedContacts?: number | null;
	leadsDueDate?: string | null;
	/** A number on the way in, a decimal string on the way back out. */
	expectedReachRate?: number | null;
	expectedCallMinutes?: number | null;
	sowRequired?: boolean;
	poRequired?: boolean;
	sowReference?: string | null;
	poReference?: string | null;
	stakeholderEmails?: string[];
	recordingDisclosure?: boolean;
	aiDisclosure?: boolean;
	transferNumber?: string | null;
	transferWindowStart?: string | null;
	transferWindowEnd?: string | null;
	dncListDocumentId?: string | null;
}

export interface CampaignRequestAgentPayload {
	language: string;
	markets?: string[];
	kind: RequestAgentKind;
	label: string;
	sourceAgentId?: string | null;
	callFlowDocumentId?: string | null;
	/** Must be active and speak this row's language — the API checks, as no foreign key can. */
	voiceId?: string | null;
	/** Sent with no `voiceId` to ask for more voices. Null withdraws the ask. */
	voiceRequestNote?: string | null;
}

/** `position` is absent and stays absent — the order is the order they were added in. */
export interface CampaignRequestMarketPayload {
	market: string;
	/** Required and without a default, exactly as the column is. */
	timezone: string;
	dialStartDate?: string | null;
	dialEndDate?: string | null;
	/** HH:MM or HH:MM:SS. */
	callingWindowStart?: string | null;
	callingWindowEnd?: string | null;
	callingDays?: number[];
	originNumberId?: number | null;
	redialIntervalHours?: number | null;
	maxAttemptsPerPerson?: number | null;
	agentId?: string | null;
	/** The CRM group this market dials. Bound during preparation, once the list has been imported. */
	leadGroupId?: string | null;
}

/** `isSystem` and `source` are absent: provenance is recorded by the API, not asserted by a screen. */
export interface CampaignRequestOutcomePayload {
	name: string;
	description?: string;
	isConversion?: boolean;
	notifyOnMatch?: boolean;
}

export interface CampaignRequestCaptureFieldPayload {
	title: string;
	description?: string;
	/** Derived from the title when omitted, and never rederived afterwards. */
	appearsAs?: string;
	enabled?: boolean;
}

export interface CampaignRequestNotificationPayload {
	toTemplate?: string | null;
	bccTemplate?: string | null;
	subjectTemplate?: string | null;
	clientNote?: string | null;
	/** Null asks for the platform default; a string is the client's own email, saved as written. */
	bodyTemplate?: string | null;
	fallbackEmail?: string | null;
	excludeContactsWithoutRecipient?: boolean;
}

/** Both are ticks, never dates — the timestamp behind them is the API's. */
export interface SubmitCampaignRequestPayload {
	leadConsentConfirmed?: boolean;
	estimatesAcknowledged?: boolean;
}

/**
 * The same two ticks, sent the moment they are made rather than at submit.
 *
 * Neither can be withdrawn: `false` and an omitted key both mean "nothing to record here", and
 * the API fills nulls only. Unticking a box that has already been stamped is not a thing the
 * wire can express, which is why the checkbox behind it is disabled once the date exists.
 */
export interface AttestCampaignRequestPayload {
	leadConsentConfirmed?: boolean;
	estimatesAcknowledged?: boolean;
}

/** Every market on the request in one write. All of them land, or none of them do. */
export interface SaveCampaignRequestMarketsPayload {
	markets: CampaignRequestMarketPayload[];
}

/** What one market was refused for, as `PUT /markets` reports it. */
export interface CampaignRequestMarketRefusal {
	/** ISO 3166-1 alpha-2, matching the card on screen. */
	market: string;
	errors: string[];
}

export interface ConfirmCampaignRequestPayload {
	quoteId: string;
	name: string;
	role: string;
	leadConsentConfirmed?: boolean;
	estimatesAcknowledged?: boolean;
}

export interface PriceBookPayload {
	code: PriceCode;
	accountId?: string | null;
	market?: string | null;
	/**
	 * A decimal string, and the API insists on one: it validates with a regex that rejects any
	 * JSON number outright. Money must not round-trip through a float.
	 */
	amount: string;
	unit?: PriceUnit;
	currency?: string;
	note?: string | null;
}

// ---------------------------------------------------------------------------------------------
// Query params
// ---------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------
// Keys, and the two helpers every hook below shares
// ---------------------------------------------------------------------------------------------

export const campaignRequestsKeys = {
	all: ["campaignRequests"] as const,
	lists: () => [...campaignRequestsKeys.all, "list"] as const,
	details: () => [...campaignRequestsKeys.all, "detail"] as const,
	detail: (id: string) => [...campaignRequestsKeys.details(), id] as const,
	markets: (id: string) => [...campaignRequestsKeys.detail(id), "markets"] as const,
	outcomes: (id: string) => [...campaignRequestsKeys.detail(id), "outcomes"] as const,
	events: (id: string) => [...campaignRequestsKeys.detail(id), "events"] as const,
	quotes: (id: string) => [...campaignRequestsKeys.detail(id), "quotes"] as const,
	rates: (id: string) => [...campaignRequestsKeys.detail(id), "rates"] as const,
	leadAnalysis: (id: string, repVariable?: string | null) =>
		[...campaignRequestsKeys.detail(id), "lead-analysis", { repVariable: repVariable ?? null }] as const,
	marketGuidance: (id: string) => [...campaignRequestsKeys.markets(id), "guidance"] as const,
	cloneable: () => [...campaignRequestsKeys.all, "cloneable"] as const,
	clonePreview: (sourceId: string) => [...campaignRequestsKeys.cloneable(), sourceId] as const,
	/**
	 * The bare roots are here as well as the parameterised keys because `useCachedTableStateWithUrl`
	 * takes a `readonly string[]` and appends its own query object — a key ending in `{ params }`
	 * cannot be handed to it, and invalidation wants the root anyway.
	 */
	priceBooks: () => [...campaignRequestsKeys.all, "price-book"] as const,
	priceBook: (accountId?: string | null) => [...campaignRequestsKeys.priceBooks(), { accountId }] as const,
	adminQueues: () => [...campaignRequestsKeys.all, "admin-queue"] as const,
};

/**
 * The API returns either a string message or a NestJS validation array. Pricing failures ("no
 * rate configured for: …") and refused transitions arrive as a 422 and must reach the operator
 * verbatim, so never swallow the payload in favour of the fallback.
 */
export function campaignRequestErrorMessage(error: any, fallback: string): string {
	const message = error?.response?.data?.message ?? error?.response?.data?.errors;
	if (Array.isArray(message)) return message.join(" ");
	return message || fallback;
}

/**
 * The per-market half of a refused `PUT /markets`, when there is one.
 *
 * A bulk write is refused as a set, and the point of refusing it as a set is that the screen can
 * put each complaint back on the card that caused it. Anything else — a network failure, a 403,
 * a body the DTO refused before the handler ran — has no market breakdown, so this answers with
 * an empty list and the caller falls back to the message.
 */
export function campaignRequestMarketRefusals(error: any): CampaignRequestMarketRefusal[] {
	const markets = error?.response?.data?.markets;
	if (!Array.isArray(markets)) return [];

	return markets.filter(
		(entry: any): entry is CampaignRequestMarketRefusal =>
			typeof entry?.market === "string" && Array.isArray(entry?.errors)
	);
}

/**
 * Array params are repeated keys — `?state=draft&state=quoted` — because that is what the API's
 * `@Transform(toArray)` reads. A comma-joined value arrives as one bogus enum member and 400s.
 */
function toQueryString(params: Record<string, unknown>): string {
	const search = new URLSearchParams();

	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;

		if (Array.isArray(value)) {
			for (const item of value) {
				if (item !== undefined && item !== null && item !== "") search.append(key, String(item));
			}
			continue;
		}

		search.append(key, String(value));
	}

	const query = search.toString();
	return query ? `?${query}` : "";
}

/** Saves bytes the browser already holds. The endpoint sets Content-Disposition: attachment. */
function saveBlob(blob: Blob, filename: string): void {
	const url = globalThis.URL.createObjectURL(blob);
	const anchor = globalThis.document.createElement("a");

	anchor.href = url;
	anchor.download = filename;
	globalThis.document.body.append(anchor);
	anchor.click();
	anchor.remove();
	globalThis.URL.revokeObjectURL(url);
}

/**
 * The filename the API named, when the browser can see it. `Content-Disposition` is only readable
 * cross-origin if the server exposes it, so this is the preference and never the only source.
 */
function filenameFromDisposition(header: unknown): string | null {
	if (typeof header !== "string") return null;

	const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
	if (encoded) {
		try {
			return decodeURIComponent(encoded[1]);
		} catch {
			return encoded[1];
		}
	}

	const plain = /filename="?([^";]+)"?/i.exec(header);
	return plain ? plain[1] : null;
}

// ---------------------------------------------------------------------------------------------
// The request itself
// ---------------------------------------------------------------------------------------------

/**
 * One request and its whole graph — agents, markets, outcomes, capture fields, notification,
 * documents, events and the live quote. Every write below invalidates this key.
 */
export function useCampaignRequest(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.detail(id ?? "none"),
		refetchInterval: (query) =>
			["confirmed", "in_preparation"].includes(query.state.data?.state ?? "") ? 10_000 : false,
		queryFn: async (): Promise<CampaignRequestDetail> => {
			const response = await apiClient.get(`/campaign-requests/${id}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

export function useCreateCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CampaignRequestPayload): Promise<CampaignRequest> => {
			const response = await apiClient.post(`/campaign-requests`, data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			toast.success("Draft request created");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not create the request"));
		},
	});
}

export function useUpdateCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			data,
		}: {
			id: string;
			data: Partial<CampaignRequestPayload>;
		}): Promise<CampaignRequest> => {
			const response = await apiClient.put(`/campaign-requests/${id}`, data);
			return response.data;
		},
		onSuccess: (request) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(request.id) });
			toast.success("Request updated");
		},
		onError: (error: any) => {
			logger.error(error);
			// A frozen request refuses the write, and the API names the state that blocked it.
			toast.error(campaignRequestErrorMessage(error, "Could not update the request"));
		},
	});
}

/**
 * Record a step 4 attestation the moment it is ticked, four steps before the submit.
 *
 * UX §07 note 3 wants "a checkbox with a date stamp behind it". Before this the tick lived in the
 * browser until submit, so a client who ticked the consent box and came back tomorrow on another
 * machine had attested to nothing, and the review screen printed "Not recorded" over a box they
 * remembered ticking.
 *
 * Does not withdraw a live quote: the API records this outside `recompute()` on purpose, because
 * an attestation is not an edit to the configuration. Silent by design — the tick going green and
 * then being disabled is the confirmation, and a toast per checkbox is noise.
 */
export function useAttestCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: AttestCampaignRequestPayload }): Promise<CampaignRequest> => {
			const response = await apiClient.post(`/campaign-requests/${id}/attest`, data);
			return response.data;
		},
		onSuccess: (request) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(request.id) });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not record that confirmation"));
		},
	});
}

/**
 * Hand a draft over to us to price. The two attestations ride along as ticks; the API stamps the
 * dates behind them, and an attestation already recorded keeps its original date.
 *
 * Submitting *is* the request for a price: the API quotes it on the way through, because
 * `POST /quote` is ours alone and a client calling it gets a 403. The response comes back already
 * in `quoted` when the price book could answer, and still in `submitted` when it could not — which
 * is a gap in our rates for a super-admin to close, not something the client can act on.
 */
export function useSubmitCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data?: SubmitCampaignRequestPayload }): Promise<CampaignRequest> => {
			const response = await apiClient.post(`/campaign-requests/${id}/submit`, data ?? {});
			return response.data;
		},
		onSuccess: (request) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(request.id) });
			// A quote was very likely created behind this call, so the history moves with it.
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.quotes(request.id) });
			toast.success("Request submitted");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not submit the request"));
		},
	});
}

/**
 * Price the request against the account's rates.
 *
 * The body is optional and pricing never needs it: the agent choices are rows on the request and
 * they are what gets priced. Sending `agents` writes that step first and then prices the written
 * rows, which is what the wizard uses to finish step 2 and ask for a price in one gesture.
 */
export function useQuoteCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, agents }: { id: string; agents?: CampaignRequestAgentPayload[] }): Promise<Quote> => {
			const response = await apiClient.post(`/campaign-requests/${id}/quote`, agents ? { agents } : {});
			return response.data;
		},
		onSuccess: (_quote, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.id) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.quotes(variables.id) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			toast.success("Quote generated");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not price this request"));
		},
	});
}

/**
 * What the choices on step 2 cost this account.
 *
 * The client-readable half of the price book, and the only rate source a client has: `/price-book`
 * is cross-account and refuses everyone but a super-admin, so reading rates from there is a 403
 * that silently strips every price off step 2 — the priced option and the running total are what
 * make that step a decision rather than a form (UX §05 notes 1 and 2).
 */
export function useCampaignRequestRates(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.rates(id ?? "none"),
		queryFn: async (): Promise<CampaignRequestRate[]> => {
			const response = await apiClient.get(`/campaign-requests/${id}/rates`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * What the uploaded lead lists contain, split by market — UX §07's "What we found" table.
 *
 * `repVariable` is the notification To template as it currently reads, so the empty-rep warning
 * at §08 note 7 can be raised against a template that has not been saved yet. It is part of the
 * key, so changing the variable asks the question again rather than showing a stale count.
 */
export function useCampaignRequestLeadAnalysis(id: string | null | undefined, repVariable?: string | null) {
	return useQuery({
		queryKey: campaignRequestsKeys.leadAnalysis(id ?? "none", repVariable),
		queryFn: async (): Promise<LeadAnalysis> => {
			const response = await apiClient.get(`/campaign-requests/${id}/leads/analysis${toQueryString({ repVariable })}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * The excluded rows, with the reason on each one.
 *
 * UX §07 note 2 in one call: "silently dropping 23 leads is how you lose trust; handing them back
 * with reasons is how you keep it." Fetched as a blob and saved from the browser, like every other
 * document download here — a plain `<a href>` carries no Authorization header.
 */
export function useDownloadExcludedLeads() {
	return useMutation({
		mutationFn: async (requestId: string) => {
			const response = await apiClient.get(`/campaign-requests/${requestId}/leads/excluded`, {
				responseType: "blob",
			});

			const named = filenameFromDisposition(response.headers?.["content-disposition"]);
			saveBlob(response.data as Blob, named ?? "excluded-leads.csv");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not download the excluded rows"));
		},
	});
}

/**
 * The step 3 weekday flag, per market, with the evidence behind it.
 *
 * Advisory only. Nothing this returns may disable anything — canvas §05 rule 1: "a flag that stops
 * the wizard becomes a flag people learn to hate."
 */
export function useCampaignRequestMarketGuidance(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.marketGuidance(id ?? "none"),
		queryFn: async (): Promise<MarketGuidance[]> => {
			const response = await apiClient.get(`/campaign-requests/${id}/markets/guidance`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * The conversion email resolved against a real row of the uploaded list.
 *
 * A mutation rather than a query because the templates being previewed are the ones in the
 * editor, unsaved — having to save a half-finished template to see what it does is the loop this
 * closes. It writes nothing, and it never toasts: a preview that cannot be built is a card that
 * stays on its structure-only fallback, not an error the client has to dismiss.
 */
export function useCampaignRequestNotificationPreview() {
	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: CampaignRequestNotificationPayload;
		}): Promise<NotificationPreview> => {
			const response = await apiClient.post(`/campaign-requests/${requestId}/notification/preview`, data);
			return response.data;
		},
		onError: (error: any) => {
			logger.error(error);
		},
	});
}

/** Every version ever offered, newest first. Superseded quotes are kept, never edited. */
export function useCampaignRequestQuotes(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.quotes(id ?? "none"),
		queryFn: async (): Promise<Quote[]> => {
			const response = await apiClient.get(`/campaign-requests/${id}/quotes`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

/**
 * Record the client accepting the live quote. The IP behind the record is taken from the
 * connection by the API — a self-reported address is worth nothing in the dispute it exists for.
 */
export function useConfirmCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: ConfirmCampaignRequestPayload }): Promise<CampaignRequest> => {
			const response = await apiClient.post(`/campaign-requests/${id}/confirm`, data);
			return response.data;
		},
		onSuccess: (_request, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.id) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.quotes(variables.id) });
			toast.success("Quote confirmed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not confirm the quote"));
		},
	});
}

/** The generic guarded move. The API is the authority on which moves are legal and who may make them. */
export function useTransitionCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, state }: { id: string; state: RequestState }): Promise<CampaignRequest> => {
			const response = await apiClient.post(`/campaign-requests/${id}/transition`, { state });
			return response.data;
		},
		onSuccess: (_request, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.id) });
			queryClient.invalidateQueries({ queryKey: ["campaign-preparation", variables.id] });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.adminQueues() });
			toast.success("Request moved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not move the request"));
		},
	});
}

/**
 * Turn a confirmed request into the programme and one campaign per market. Super-admin only, and
 * idempotent: a second call reports an empty `created` rather than building everything twice.
 */
export function useProvisionCampaignRequest() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<ProvisionResult> => {
			const response = await apiClient.post(`/campaign-requests/${id}/provision`);
			return response.data;
		},
		onSuccess: (result, id) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.adminQueues() });
			toast.success(
				result.created.length > 0
					? `Provisioned ${result.created.length} market${result.created.length === 1 ? "" : "s"}`
					: "Everything was already provisioned"
			);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not provision this request"));
		},
	});
}

/** Every move this request has made, oldest first. The only record of *when* a request moved. */
export function useCampaignRequestEvents(id: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.events(id ?? "none"),
		queryFn: async (): Promise<CampaignRequestEvent[]> => {
			const response = await apiClient.get(`/campaign-requests/${id}/events`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

// ---------------------------------------------------------------------------------------------
// Agents — one row per language
// ---------------------------------------------------------------------------------------------

export function useCreateCampaignRequestAgent() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: CampaignRequestAgentPayload;
		}): Promise<CampaignRequestAgent> => {
			const response = await apiClient.post(`/campaign-requests/${requestId}/agents`, data);
			return response.data;
		},
		onSuccess: (_agent, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Language added");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not add the language"));
		},
	});
}

export function useUpdateCampaignRequestAgent() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			agentId,
			data,
		}: {
			requestId: string;
			agentId: string;
			data: Partial<CampaignRequestAgentPayload>;
		}): Promise<CampaignRequestAgent> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/agents/${agentId}`, data);
			return response.data;
		},
		onSuccess: (_agent, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Agent choice saved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not save the agent choice"));
		},
	});
}

/**
 * Dropping a language leaves any market that answered in it without an agent — the API nulls the
 * pointer rather than refusing, because a half-made decision is one the client is allowed to hold.
 */
export function useDeleteCampaignRequestAgent() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ requestId, agentId }: { requestId: string; agentId: string }) => {
			const response = await apiClient.delete(`/campaign-requests/${requestId}/agents/${agentId}`);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Language removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not remove the language"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Markets — one row per market, and each one becomes a campaign
// ---------------------------------------------------------------------------------------------

export function useUpdateCampaignRequestMarket() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			marketId,
			data,
		}: {
			requestId: string;
			marketId: string;
			data: Partial<CampaignRequestMarketPayload>;
		}): Promise<CampaignRequestMarket> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/markets/${marketId}`, data);
			return response.data;
		},
		onSuccess: (_market, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Schedule saved");
		},
		onError: (error: any) => {
			logger.error(error);
			// A dial window that ends before it starts is a 422, and the API says which one.
			toast.error(campaignRequestErrorMessage(error, "Could not save the schedule"));
		},
	});
}

/**
 * Every market on the request, written as one thing.
 *
 * Step 3 is one screen of four schedules behind one button, and saving it as four calls made the
 * screen lie: a 422 on the second market — a dial end before its start is the everyday one —
 * left the first market written, the third and fourth never attempted, and a toast naming only
 * the second. The request then held a schedule for one market out of four and nothing said so.
 *
 * The API refuses or writes the whole set in one transaction, and a refusal carries a per-market
 * breakdown so the complaint can go back on the card that caused it — read it with
 * `campaignRequestMarketRefusals`. No toast on failure: the cards say it better than a banner
 * that names one market can.
 */
export function useSaveCampaignRequestMarkets() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: SaveCampaignRequestMarketsPayload;
		}): Promise<CampaignRequestMarket[]> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/markets`, data);
			return response.data;
		},
		onSuccess: (_markets, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
		},
		onError: (error: any) => {
			logger.error(error);
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Outcomes — how a call is allowed to end
// ---------------------------------------------------------------------------------------------

export function useCampaignRequestOutcomes(requestId: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.outcomes(requestId ?? "none"),
		queryFn: async (): Promise<CampaignRequestOutcome[]> => {
			const response = await apiClient.get(`/campaign-requests/${requestId}/outcomes`);
			return response.data;
		},
		enabled: Boolean(requestId),
	});
}

/**
 * The list as we propose it, before the client edits anything: the standard set for the
 * objective, then whatever can be read out of the uploaded call flow.
 *
 * Seeds, never resets — a row the client deleted does not come back, and asking for the call flow
 * merges by name onto whatever is already there. Omitting `source` asks for both.
 */
export function useStarterCampaignRequestOutcomes() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			source,
		}: {
			requestId: string;
			source?: Extract<OutcomeSource, "starter" | "call_flow">;
		}): Promise<CampaignRequestOutcome[]> => {
			const response = await apiClient.post(
				`/campaign-requests/${requestId}/outcomes/starter`,
				source ? { source } : {}
			);
			return response.data;
		},
		onSuccess: (_outcomes, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not propose an outcome set"));
		},
	});
}

export function useCreateCampaignRequestOutcome() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: CampaignRequestOutcomePayload;
		}): Promise<CampaignRequestOutcome> => {
			const response = await apiClient.post(`/campaign-requests/${requestId}/outcomes`, data);
			return response.data;
		},
		onSuccess: (_outcome, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Outcome added");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not add the outcome"));
		},
	});
}

export function useUpdateCampaignRequestOutcome() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			outcomeId,
			data,
		}: {
			requestId: string;
			outcomeId: string;
			data: Partial<CampaignRequestOutcomePayload>;
		}): Promise<CampaignRequestOutcome> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/outcomes/${outcomeId}`, data);
			return response.data;
		},
		onSuccess: (_outcome, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
		},
		onError: (error: any) => {
			logger.error(error);
			// A system row is the platform's own disposition; the API refuses the edit with its name.
			toast.error(campaignRequestErrorMessage(error, "Could not save the outcome"));
		},
	});
}

export function useDeleteCampaignRequestOutcome() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ requestId, outcomeId }: { requestId: string; outcomeId: string }) => {
			const response = await apiClient.delete(`/campaign-requests/${requestId}/outcomes/${outcomeId}`);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Outcome removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not remove the outcome"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Capture fields — what to pull out of every conversation
// ---------------------------------------------------------------------------------------------

export function useCreateCampaignRequestCaptureField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: CampaignRequestCaptureFieldPayload;
		}): Promise<CampaignRequestCaptureField> => {
			const response = await apiClient.post(`/campaign-requests/${requestId}/capture-fields`, data);
			return response.data;
		},
		onSuccess: (_field, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Field added");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not add the field"));
		},
	});
}

/**
 * `appearsAs` is editable but never moves on its own: renaming the title leaves the variable
 * alone, because a notification template already naming the old one would silently resolve empty.
 */
export function useUpdateCampaignRequestCaptureField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			fieldId,
			data,
		}: {
			requestId: string;
			fieldId: string;
			data: Partial<CampaignRequestCaptureFieldPayload>;
		}): Promise<CampaignRequestCaptureField> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/capture-fields/${fieldId}`, data);
			return response.data;
		},
		onSuccess: (_field, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not save the field"));
		},
	});
}

export function useDeleteCampaignRequestCaptureField() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ requestId, fieldId }: { requestId: string; fieldId: string }) => {
			const response = await apiClient.delete(`/campaign-requests/${requestId}/capture-fields/${fieldId}`);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Field removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not remove the field"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Notification — a singleton, and null until the client has configured it
// ---------------------------------------------------------------------------------------------

/** PUT upserts: there is nothing to create and nothing to choose between, only a row or no row. */
export function useUpdateCampaignRequestNotification() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			data,
		}: {
			requestId: string;
			data: CampaignRequestNotificationPayload;
		}): Promise<CampaignRequestNotification> => {
			const response = await apiClient.put(`/campaign-requests/${requestId}/notification`, data);
			return response.data;
		},
		onSuccess: (_notification, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			toast.success("Notification saved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not save the notification"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------------------------

/**
 * Upload a document.
 *
 * The kind is a path segment rather than a body field: the API's validation pipe never sees a
 * multipart body, so a `kind` sent in the form would be dropped in silence and every document
 * would land as the same type. The file itself must go up as `multipart/form-data`.
 *
 * `agentId` travels in the query string for the same reason, and matters for the same one:
 * agents are keyed by language, so a call flow uploaded without naming its agent leaves the API
 * to work out which language the script is in. Send it wherever the screen knows.
 */
export function useUploadCampaignRequestDocument() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			requestId,
			kind,
			file,
			agentId,
		}: {
			requestId: string;
			kind: DocumentKind;
			file: File;
			/** The agent a call flow belongs to, where the screen knows which one that is. */
			agentId?: string | null;
		}): Promise<CampaignRequestDocument> => {
			const formData = new FormData();
			formData.append("file", file);

			const response = await apiClient.post(
				`/campaign-requests/${requestId}/documents/${kind}${toQueryString({ agent: agentId })}`,
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } }
			);
			return response.data;
		},
		onSuccess: (_document, variables) => {
			// Four of the five blockers are answered from what is attached, so the whole graph moves.
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			toast.success("Document uploaded");
		},
		onError: (error: any) => {
			logger.error(error);
			// 413 over the 25 MiB ceiling, 415 where the bytes are not what this kind may be.
			toast.error(campaignRequestErrorMessage(error, "Could not upload the document"));
		},
	});
}

/**
 * Download a document.
 *
 * The response is an attachment of decrypted bytes, so it is fetched as a blob and saved from the
 * browser rather than linked to: a plain `<a href>` would carry no Authorization header, and the
 * API refuses it.
 */
export function useDownloadCampaignRequestDocument() {
	return useMutation({
		mutationFn: async ({
			requestId,
			document,
		}: {
			requestId: string;
			document: Pick<CampaignRequestDocument, "id" | "originalFilename">;
		}) => {
			const response = await apiClient.get(`/campaign-requests/${requestId}/documents/${document.id}/download`, {
				responseType: "blob",
			});

			const named = filenameFromDisposition(response.headers?.["content-disposition"]);
			saveBlob(response.data as Blob, named ?? document.originalFilename);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not download the document"));
		},
	});
}

/**
 * Destroys the bytes. The metadata row is soft-deleted by the API so the record of what was held,
 * and who read it, outlives the file.
 */
export function useDeleteCampaignRequestDocument() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ requestId, documentId }: { requestId: string; documentId: string }) => {
			const response = await apiClient.delete(`/campaign-requests/${requestId}/documents/${documentId}`);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			toast.success("Document removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not remove the document"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Clone — "repeat what worked", the wizard's default way in
// ---------------------------------------------------------------------------------------------

/** The previous campaigns this account can repeat, newest first. */
export function useCloneableCampaignRequests(enabled = true) {
	return useQuery({
		queryKey: campaignRequestsKeys.cloneable(),
		queryFn: async (): Promise<CloneableRequest[]> => {
			const response = await apiClient.get(`/campaign-requests/cloneable`);
			return response.data;
		},
		enabled,
	});
}

/**
 * What repeating one would bring across, before anything is written. Answered without a target,
 * so the dropdown can be read on a request that does not exist yet.
 */
export function useCampaignRequestClonePreview(sourceId: string | null | undefined) {
	return useQuery({
		queryKey: campaignRequestsKeys.clonePreview(sourceId ?? "none"),
		queryFn: async (): Promise<CloneManifest> => {
			const response = await apiClient.get(`/campaign-requests/cloneable/${sourceId}`);
			return response.data;
		},
		enabled: Boolean(sourceId),
	});
}

export function useCloneCampaignRequestFrom() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ requestId, sourceId }: { requestId: string; sourceId: string }): Promise<CloneManifest> => {
			const response = await apiClient.post(`/campaign-requests/${requestId}/clone-from/${sourceId}`);
			return response.data;
		},
		onSuccess: (manifest, variables) => {
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.detail(variables.requestId) });
			queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.lists() });
			toast.success(`Copied from ${manifest.source.title}`);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not repeat that campaign"));
		},
	});
}

// ---------------------------------------------------------------------------------------------
// Price book — super-admin only, cross-account, never shown to a client
// ---------------------------------------------------------------------------------------------

/**
 * The rates in force. Pass an account to read them as that account sees them: its overrides plus
 * the platform defaults it falls back to. A 403 is expected for everyone else and must not retry.
 */
export function usePriceBook(accountId?: string | null, enabled = true) {
	return useQuery({
		enabled,
		queryKey: campaignRequestsKeys.priceBook(accountId ?? null),
		queryFn: async (): Promise<PriceBookItem[]> => {
			const query = toQueryString({ account: accountId });
			const response = await apiClient.get(`/price-book${query}`);
			return response.data;
		},
		retry: false,
	});
}

/** Upserts on (code, account, market) — that triple is what a rate *is*, so no id is needed. */
export function useUpsertPriceBookItem() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: PriceBookPayload): Promise<PriceBookItem> => {
			const response = await apiClient.put(`/price-book`, data);
			return response.data;
		},
		// Awaited, so `mutateAsync` resolves on refreshed data: the form closes on the amount that
		// was saved, and re-opening it cannot capture the stale one and write it back.
		onSuccess: async () => {
			// New rates apply to future quotes only; nothing already confirmed moves.
			await queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.priceBooks() });
			toast.success("Rate saved");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not save the rate"));
		},
	});
}

export function useDeletePriceBookItem() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.delete(`/price-book/${id}`);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: campaignRequestsKeys.priceBooks() });
			toast.success("Rate removed");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(campaignRequestErrorMessage(error, "Could not remove the rate"));
		},
	});
}
